// src/api/nauphilusApi.ts
import axios, { AxiosResponse } from 'axios';
import { getTokenConfig } from '../../../../auth';
import { NauphilusReportRow } from '../core/nauphilus.types';

interface VerifierResponse {
  signedUrl: string;
}

// ------------------- FETCH INTERVIEWS -------------------
export const fetchInterviews = async (
  page: number,
  limit: number,
  searchValue: string,
  searchCustomer: string, // customerId
  createdAtInit: string,
  createdAtEnd: string,
  orderType: string,
  orderDirection: string
): Promise<{ rows: NauphilusReportRow[]; totalPages: number; timezone: string }> => {
  try {
    const config = await getTokenConfig();

    // 🔹 Mapa de columnas virtuales -> reales
    const ORDER_COLUMN_MAP: Record<string, string> = {
      fechaRegistro: "createdAt",
      horaRegistro: "createdAt",
      solicitud: "transaction",
      socioComercial: "customerId",   // ⚡ ahora usamos customerId
      estadoEntrevista: "statusCode",
    };

    const dbColumn = ORDER_COLUMN_MAP[orderType] || "createdAt";
    const dbDirection = orderDirection?.toUpperCase() === "ASC" ? "ASC" : "DESC";

    const params = new URLSearchParams();
    params.append("limit", String(limit));
    params.append("page", String(page));
    if (searchValue) params.append("search", searchValue);
    if (searchCustomer) params.append("customerId", searchCustomer); // ⚡ usar siempre customerId
    if (createdAtInit) params.append("createdAtDateInit", createdAtInit);
    if (createdAtEnd) params.append("createdAtDateEnd", createdAtEnd);
    params.append("order", `${dbColumn},${dbDirection}`);

    const query = `${process.env.REACT_APP_API_URL}/admin/nauphilus-report?${params.toString()}`;
    const response = await axios.get(query, config);

    const rows = response.data.data.requests;
    const total = response.data.data.total;
    const timezone = response.data.data.timezone;

    return { rows, totalPages: Math.ceil(total / limit), timezone };
  } catch (err) {
    throw err;
  }
};

// ------------------- FETCH MODAL DATA -------------------
export const fetchModalData = async (id: number) => {
  const config = await getTokenConfig();
  const query = `${process.env.REACT_APP_API_URL}/detail/nauphilus-report?id=${id}`;
  const response = await axios.get(query, config);
  return response.data.data.request;
};

// ------------------- FETCH CUSTOMERS -------------------
export const fetchCustomers = async () => {
  const config = await getTokenConfig();
  const queryString = `${process.env.REACT_APP_API_URL}/admin/list-customers`;
  const response = await axios.get(queryString, config);
  return response.data.data.customers;
};

// ------------------- FETCH EXCEL REPORT -------------------
export const fetchExcelReport = async (
  searchValue: string,
  searchCustomer: string, // customerId
  createdAtInit: string,
  createdAtEnd: string,
  orderType: string,
  orderDirection: string
): Promise<string | null> => {
  try {
    const config = await getTokenConfig();

    const ORDER_COLUMN_MAP: Record<string, string> = {
      fechaRegistro: "createdAt",
      horaRegistro: "createdAt",
      solicitud: "transaction",
      socioComercial: "customerId", // ⚡ también customerId
      estadoEntrevista: "statusCode",
    };

    const dbColumn = ORDER_COLUMN_MAP[orderType] || "createdAt";
    const dbDirection = orderDirection?.toUpperCase() === "ASC" ? "ASC" : "DESC";

    const params = new URLSearchParams();
    if (searchValue) params.append("search", searchValue);
    if (searchCustomer) params.append("customerId", searchCustomer); // ⚡
    if (createdAtInit) params.append("createdAtDateInit", createdAtInit);
    if (createdAtEnd) params.append("createdAtDateEnd", createdAtEnd);
    params.append("order", `${dbColumn},${dbDirection}`);

    // Paso 1: disparar generación
    const query = `${process.env.REACT_APP_API_URL}/download/admin/nauphilus-report?${params.toString()}`;
    const initialResponse = await axios.get(query, config);

    const nameReport = initialResponse.data?.name;
    if (!nameReport) return null;

    // Espera inicial
    await new Promise((resolve) => setTimeout(resolve, 5000));

    // Paso 2: polling
    let signedUrl: string | null = null;
    for (let attempt = 0; attempt < 20 && !signedUrl; attempt++) {
      signedUrl = await verifyFileReady(config, nameReport);
      if (!signedUrl) await new Promise((resolve) => setTimeout(resolve, 5000));
    }

    return signedUrl;
  } catch (err) {
    console.error("❌ Error en fetchExcelReport:", err);
    return null;
  }
};



// ------------------- VERIFY FILE READY -------------------
const verifyFileReady = async (config: any, nameReport: string): Promise<string | null> => {
  try {
    const verifyUrl = `${process.env.REACT_APP_API_URL}/download/admin/nauphilus-report?mode=check&filename=${nameReport}`;
    const response: AxiosResponse<VerifierResponse> = await axios.get(verifyUrl, config);
    return response.data?.signedUrl ?? null;
  } catch (error: any) {
    if (error.response?.status === 404) {
      // No mostramos error rojo, solo aviso de que no está listo
      return null;
    }
    console.error("❌ Error inesperado en verifyFileReady:", error);
    throw error;
  }
};
